import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

// pdfjs requires DOMMatrix at module-load time, which doesn't exist in SSR.
// Lazy-load the library + worker URL inside the effect (browser-only) so this
// component is safe to render through the SSR pass.

interface Props {
  url: string;
  className?: string;
}

export function PdfThumbnail({ url, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let task: import("pdfjs-dist").PDFDocumentLoadingTask | null = null;

    (async () => {
      try {
        // Legacy build — same floor rationale as classroom/SharedDocument.tsx.
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
        // @ts-ignore — Vite resolves this at build time.
        const pdfWorker = (await import("pdfjs-dist/legacy/build/pdf.worker.mjs?url")).default;
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

        task = pdfjs.getDocument({ url, disableAutoFetch: true, disableStream: true });
        const doc = await task.promise;
        if (cancelled) return;
        const page = await doc.getPage(1);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        const targetWidth = 88;
        const scale = targetWidth / viewport.width;
        const scaled = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = Math.ceil(scaled.width);
        canvas.height = Math.ceil(scaled.height);
        await page.render({ canvasContext: ctx, viewport: scaled, canvas }).promise;
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      task?.destroy?.();
    };
  }, [url]);

  if (failed) {
    return (
      <div
        className={cn(
          "flex size-12 shrink-0 items-center justify-center rounded-lg bg-rose-500/15 text-rose-500",
          className,
        )}
      >
        <FileText className="size-5" />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={cn(
        "block h-16 w-12 shrink-0 rounded-md border border-border bg-white object-cover",
        className,
      )}
      aria-hidden
    />
  );
}
